import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../api/axios.tsx";
import Button from "../../components/ui/button.tsx";
import Input from "../../components/ui/input.tsx";
import Label from "../../components/ui/label.tsx";
import { GoogleLogin } from "@react-oauth/google";
import { Eye, EyeOff } from "lucide-react";
import Checkbox from "../../components/ui/checkbox.tsx";

type Mode = "signin" | "signup";

const Auth: React.FC = () => {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (mode === "signup") {
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          return;
        }
        if (!acceptTerms) {
          setError("You must accept the Terms & Conditions");
          return;
        }
      }
      let res;
      if (mode === "signin") {
        res = await api.post("/auth/login", { email, password });
      } else {
        res = await api.post("/auth/register", { email, password });
      }
      localStorage.setItem("user", JSON.stringify(res.data));
      const shouldVerify = res?.data?.is_email_verified === false;
      navigate(shouldVerify ? "/verify-email" : "/dashboard");
    } catch (err: any) {
      console.error("Auth error", err);
      const msg = err?.response?.data?.detail || "Something went wrong";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleSuccess = (response: any) => {
    const idToken = response.credential;
    api
      .post(
        "/auth/google/token",
        { id_token: idToken },
        { headers: { "Content-Type": "application/json" } }
      )
      .then((r) => {
        const data = r.data;
        if (data) {
          localStorage.setItem("user", JSON.stringify(data));
          const shouldVerify = data?.is_email_verified === false;
          navigate(shouldVerify ? "/verify-email" : "/dashboard");
        }
      })
      .catch((err) => {
        console.error("Google auth failed", err);
        setError("Google authentication failed");
      });
  };

  useEffect(() => {
    let user =
      localStorage.getItem("user") &&
      localStorage.getItem("user") != null &&
      JSON.parse(localStorage.getItem("user")!);

    if (user?.is_email_verified) {
      navigate("/dashboard");
    }
  }, [navigate]);

  return (
    <div className="page flex items-center justify-center p-4">
      <div className="w-full max-w-md p-8 card-glass relative z-10">
        <div className="text-center">
          <h1 className="heading-2 text-center text-glow">
            {mode === "signin" ? "Welcome back" : "Join Budiee"}
          </h1>
          <p className="subheading">
            {mode === "signin"
              ? "Access your dashboard and keep finances on track."
              : "Start organizing your money with clarity and control."}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2 bg-white/5 border border-white/10 rounded-lg p-1">
          <button
            className={[
              "px-3 py-2 rounded-md text-sm font-semibold",
              mode === "signin"
                ? "bg-white/10 text-white"
                : "text-white/80 hover:bg-white/10",
            ].join(" ")}
            onClick={() => setMode("signin")}
          >
            Sign in
          </button>
          <button
            className={[
              "px-3 py-2 rounded-md text-sm font-semibold",
              mode === "signup"
                ? "bg-white/10 text-white"
                : "text-white/80 hover:bg-white/10",
            ].join(" ")}
            onClick={() => setMode("signup")}
          >
            Create account
          </button>
        </div>

        <form className="flex flex-col mt-6" onSubmit={onSubmit}>
          <Label htmlFor="email" className="mb-1">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mb-4"
          />

          <Label htmlFor="password" className="mb-1">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mb-2 pr-10"
            />
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
              onClick={() => setShowPassword((s) => !s)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {mode === "signup" && (
            <>
              <Label htmlFor="confirm" className="mb-1">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirm"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="mb-2 pr-10"
                />
                <button
                  type="button"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                  onClick={() => setShowConfirm((s) => !s)}
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <label className="mt-1 mb-2 inline-flex items-center gap-2 text-sm text-white/80 select-none">
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) =>
                    setAcceptTerms((e.target as HTMLInputElement).checked)
                  }
                  required
                />
                I agree to the{" "}
                <Link to="/terms" className="text-blue-400 hover:underline">
                  Terms & Conditions
                </Link>
              </label>
            </>
          )}

          {mode === "signin" && (
            <div className="text-right text-sm mt-1">
              <Link
                to="/forgot-password"
                className="text-blue-400 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          )}
          {error && (
            <div className="text-red-400 text-sm mt-1" role="alert">
              {error}
            </div>
          )}
          <Button
            type="submit"
            className="mt-4 btn-primary w-full"
            disabled={loading}
          >
            {loading
              ? mode === "signin"
                ? "Signing in..."
                : "Creating..."
              : mode === "signin"
              ? "Sign in"
              : "Create account"}
          </Button>
        </form>

        <div className="mt-6 flex items-center gap-3">
          <div className="h-px bg-white/15 w-full" />
          <span className="text-xs text-white/70 text-nowrap">Or</span>
          <div className="h-px bg-white/15 w-full" />
        </div>
        <div className="mt-4 flex flex-col gap-3 items-center">
          <GoogleLogin
            onSuccess={onGoogleSuccess}
            onError={() => setError("Google authentication failed")}
            theme="filled_black"
            size="large"
            text="continue_with"
            shape="pill"
            width="360"
          />
        </div>
      </div>
    </div>
  );
};

export default Auth;
